<script lang="ts">
  import { TwistStack, TwistBox } from '@twist/core';
  import { modals } from '$lib/stores/modals';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import Modal from '$components/Modals/Modal.svelte';
  import StandardSearchContent from './StandardSearchContent.svelte';
  import AiSearchContent from './AiSearchContent.svelte';
  import Times from '@twist/icons/lib/Times.svg?component';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import TabBar from '$components/Common/TabBar.svelte';
  import { SearchTab } from './types';

  const tabs = [
    { id: SearchTab.Standard, label: 'Search' },
    { id: SearchTab.AI, label: 'AI Chat' }
  ];

  let activeTab = $state(SearchTab.Standard);

  const handleTabChange = (tabId: SearchTab) => {
    activeTab = tabId;
  };

  const handleClose = () => {
    modals.close('search');
  };

  $effect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      // Close on Escape
      if (event.key === 'Escape') {
        handleClose();
      }
      // Focus search input on / key
      if (
        event.key === '/' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !(event.target instanceof HTMLInputElement)
      ) {
        event.preventDefault();
        const input = document.querySelector<HTMLInputElement>('.active .search-input');
        if (input) {
          input.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

<Modal modalKey="search" size="fullscreen" ariaLabel="Search">
  {#snippet children({ onCloseClicked })}
    <div class="wrapper">
      <div class="modal-layout">
        <div class="modal-header">
          <TwistStack direction="row" align="center" justify="flex-end" wrap="nowrap">
            <BlockButton
              variant="ghost"
              ariaLabel="Close search"
              size="lg"
              py={toFluidVarInput(18, 18)}
              px={toFluidVarInput(9, 9)}
              onclick={onCloseClicked}
              motion="never"
            >
              {#snippet icon()}
                <Times style="width: 20px; height: 20px;" />
              {/snippet}
            </BlockButton>
          </TwistStack>
          <TwistBox marginTop={toFluidVarInput(30, 30)} marginBottom={toFluidVarInput(20, 20)}>
            <TabBar
              {tabs}
              activeTabId={activeTab}
              onTabChange={handleTabChange}
              ariaLabel="Choose search interface"
            />
          </TwistBox>
        </div>
        <div class="search-content">
          <div
            class="search-tab"
            id="search-tab-standard"
            class:active={activeTab === SearchTab.Standard}
          >
            <StandardSearchContent />
          </div>
          <div class="search-tab" id="search-tab-ai" class:active={activeTab === SearchTab.AI}>
            <AiSearchContent />
          </div>
        </div>
      </div>
    </div>
  {/snippet}
</Modal>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';

  .wrapper {
    height: 100%;
    background: $background-depth-0-color;
  }

  .modal-layout {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 24px;
    @include at('sm') {
      padding: 40px;
    }
  }

  .modal-header {
    margin-bottom: 24px;
    color: $text-default-color;
    width: 100%;
  }

  .search-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .search-tab {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    &.active {
      opacity: 1;
      pointer-events: auto;
    }
  }
</style>
